import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck, Camera, Star, Loader2, Upload, CheckCircle } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

function CleanerContent() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inspecting, setInspecting] = useState(null);
  const [form, setForm] = useState({ condition_rating: 5, has_damage: false, notes: '', photo_paths: [] });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const res = await api.get('/cleaner/jobs');
      setJobs(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchJobs(); }, []);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, photo_paths: [...f.photo_paths, res.data.path] }));
    } catch (e) {
      setMsg('Photo upload failed');
    }
    setUploading(false);
  };

  const submitInspection = async () => {
    setSubmitting(true);
    setMsg('');
    try {
      const res = await api.post(`/cleaner/inspect/${inspecting}`, form);
      setMsg(res.data.message);
      setInspecting(null);
      setForm({ condition_rating: 5, has_damage: false, notes: '', photo_paths: [] });
      fetchJobs();
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#CC5A3D]" /></div>;

  return (
    <div data-testid="cleaner-dashboard">
      <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-6">My Cleaning Jobs</h1>

      {msg && <div className="mb-4 p-3 bg-[#F0EBE1] border border-[#E6D5B8] text-sm text-[#1F2A25] rounded" data-testid="cleaner-msg">{msg}</div>}

      {inspecting ? (
        <div className="bg-white border border-[#E6D5B8] p-6 rounded-lg" data-testid="inspection-form">
          <h2 className="text-xl font-heading font-medium text-[#1F2A25] mb-4 flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Inspection Form</h2>

          <div className="space-y-5">
            <div>
              <Label className="text-sm text-[#1F2A25]">Condition Rating</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map(r => (
                  <button key={r} onClick={() => setForm({...form, condition_rating: r})} className={`w-10 h-10 rounded flex items-center justify-center transition-colors ${form.condition_rating >= r ? 'bg-[#CC5A3D] text-white' : 'bg-[#F0EBE1] text-[#6B726A]'}`} data-testid={`rating-${r}`}>
                    <Star className="w-5 h-5" fill={form.condition_rating >= r ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.has_damage} onCheckedChange={v => setForm({...form, has_damage: v})} data-testid="damage-toggle" />
              <Label className="text-sm text-[#1F2A25]">Damage Found</Label>
            </div>

            {form.has_damage && (
              <>
                <div>
                  <Label className="text-sm text-[#1F2A25]">Upload Photos</Label>
                  <div className="mt-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadPhoto(e.target.files[0]); }} />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-[#E6D5B8]" data-testid="upload-photo-btn">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                      {uploading ? 'Uploading...' : 'Add Photo'}
                    </Button>
                    {form.photo_paths.length > 0 && (
                      <p className="text-xs text-[#6B726A] mt-2">{form.photo_paths.length} photo(s) uploaded</p>
                    )}
                  </div>
                </div>
              </>
            )}

            <div>
              <Label className="text-sm text-[#1F2A25]">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Add any notes about the property condition..." className="mt-1 border-[#E6D5B8] min-h-[100px]" data-testid="inspection-notes" />
            </div>

            <div className="flex gap-3">
              <Button onClick={() => { setInspecting(null); setForm({ condition_rating: 5, has_damage: false, notes: '', photo_paths: [] }); }} variant="outline" className="border-[#E6D5B8]" data-testid="cancel-inspection-btn">Cancel</Button>
              <Button onClick={submitInspection} disabled={submitting} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm" data-testid="submit-inspection-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submit Inspection
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.length === 0 && <p className="text-[#6B726A] py-8 text-center bg-white border border-[#E6D5B8] rounded-lg">No jobs assigned to you</p>}
          {jobs.map(j => (
            <div key={j.id} className="bg-white border border-[#E6D5B8] p-5 rounded-lg flex items-center justify-between" data-testid={`job-${j.id}`}>
              <div>
                <h3 className="font-medium text-[#1F2A25]">{j.caravan_name}</h3>
                <p className="text-sm text-[#6B726A]">{j.park_name} &middot; Guest: {j.guest_name}</p>
                <p className="text-xs text-[#6B726A]">Checkout: {j.check_out}</p>
                <Badge variant="secondary" className="mt-2 capitalize bg-[#F0EBE1] text-[#1E3F33]">{j.status?.replace(/_/g, ' ')}</Badge>
              </div>
              <Button onClick={() => setInspecting(j.id)} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm" data-testid={`inspect-btn-${j.id}`}>
                <ClipboardCheck className="w-4 h-4 mr-2" /> Inspect
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CleanerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !['owner', 'cleaner'].includes(user.role))) navigate('/login');
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <DashboardLayout>
      <CleanerContent />
    </DashboardLayout>
  );
}
